/**
 * DxfEmptyHero — high-impact empty state for the Tecnico hub.
 *
 * Replaces the generic ``SurfaceEmptyState`` with an engineering-context
 * hero that communicates the value of the surface (DXF/DWG → blocks →
 * BOM Excel workflow). Inline SVG blueprint illustration uses
 * ``currentColor`` so it adapts to dark mode + brand palette tweaks
 * without an asset round-trip.
 *
 * Used by ``DxfPreviewStage`` when no file is selected (both first-load
 * and after-delete states).
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ClipboardCheck,
  Compass,
  FileSpreadsheet,
  GitBranch,
  type LucideIcon,
  ScanLine,
  Upload,
} from "lucide-react";
import { cn } from "@ando/ui";

export interface DxfEmptyHeroProps {
  readonly totalCount?: number;
  readonly isFiltered?: boolean;
  readonly onUpload?: () => void;
  readonly onConnectDrive?: () => void;
  /**
   * Sprint 1 (Jul 2026) — open the DFM rules section. Surfaced as a
   * secondary link below the two primary CTAs so a designer with a
   * manufacturability checklist to load has an obvious path without
   * cluttering the hero's two-CTA rhythm.
   */
  readonly onOpenDfmRules?: () => void;
  readonly className?: string;
}

interface WorkflowStep {
  readonly id: string;
  readonly icon: LucideIcon;
  readonly title: ReturnType<typeof msg>;
  /** CAD format names stay literal (not localizable proper nouns). */
  readonly body: ReturnType<typeof msg> | string;
}

const WORKFLOW_STEPS: ReadonlyArray<WorkflowStep> = [
  {
    id: "upload",
    icon: Upload,
    title: msg`Carica`,
    body: "DXF · DWG · STEP · IGES · STL · PDF",
  },
  {
    id: "extract",
    icon: ScanLine,
    title: msg`Estrazione`,
    body: msg`Blocchi, quote, fori, geometria 3D`,
  },
  {
    id: "bom",
    icon: FileSpreadsheet,
    title: msg`Distinta`,
    body: msg`Excel pronta in 1 click`,
  },
  {
    id: "revisions",
    icon: GitBranch,
    title: msg`Revisioni`,
    body: msg`Confronto v→v automatico`,
  },
];

export function DxfEmptyHero({
  totalCount = 0,
  isFiltered = false,
  onUpload,
  onConnectDrive,
  onOpenDfmRules,
  className,
}: DxfEmptyHeroProps) {
  const { i18n } = useLingui();
  // Two variants: cold-start (no drawings at all) vs filtered (has
  // drawings but search/category narrowed to zero). The cold-start
  // pushes the workflow education; the filtered variant pitches a clear
  // CTA back to "Tutti i disegni".
  if (isFiltered && totalCount > 0) {
    return (
      <div
        className={cn(
          "flex min-h-[420px] flex-col items-center justify-center gap-3 p-10 text-center",
          className,
        )}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ando-orange-soft text-ando-orange">
          <Compass size={22} strokeWidth={1.5} aria-hidden />
        </div>
        <div className="space-y-1">
          <h2 className="font-semibold text-[16px] text-ink-1">
            <Trans>Nessun disegno corrisponde ai filtri</Trans>
          </h2>
          <p className="max-w-md text-[13px] text-ink-3">
            <Trans>
              Hai{" "}
              <strong className="font-semibold text-ink-2 tabular-nums">
                {totalCount}
              </strong>{" "}
              disegni indicizzati in totale. Allarga la ricerca o rimuovi i
              filtri per vederli tutti.
            </Trans>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center gap-6 p-10",
        // Match the populated canvas dimensions so first selection
        // doesn't cause a layout shift.
        "min-h-[520px]",
        className,
      )}
    >
      <BlueprintIllustration />

      <div className="max-w-md space-y-2 text-center">
        <h2 className="font-semibold text-[20px] text-ink-1">
          <Trans>Porta i disegni meccanici in Ando</Trans>
        </h2>
        <p className="text-[13px] text-ink-3 leading-relaxed">
          <Trans>
            Trascina un file CAD o una tavola PDF — supportati{" "}
            <span className="font-mono text-ink-2">
              DXF · DWG · STEP · IGES · STL · PDF
            </span>
            . Ando estrae blocchi, quote, fori, cartiglio e geometria 3D — la
            distinta è pronta per Excel in un click.
          </Trans>
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        {onUpload ? (
          <button
            type="button"
            onClick={onUpload}
            className="inline-flex items-center gap-2 rounded-lg bg-ando-orange px-4 py-2 font-medium text-[13px] text-white shadow-[0_1px_2px_rgba(15,17,21,0.08)] transition-colors hover:bg-ando-orange/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
          >
            <Upload size={14} strokeWidth={1.75} aria-hidden />
            <Trans>Carica un disegno</Trans>
          </button>
        ) : null}
        {onConnectDrive ? (
          <button
            type="button"
            onClick={onConnectDrive}
            className="inline-flex items-center gap-2 rounded-lg border border-hairline bg-card px-4 py-2 font-medium text-[13px] text-ink-2 transition-colors hover:bg-card/85 hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
          >
            <Trans>Collega un drive</Trans>
          </button>
        ) : null}
      </div>

      {onOpenDfmRules ? (
        <button
          type="button"
          onClick={onOpenDfmRules}
          className="mt-1 inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-medium text-[12px] text-ink-3 hover:text-ando-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
          style={{ minHeight: 32 }}
        >
          <ClipboardCheck size={13} strokeWidth={1.75} aria-hidden />
          <Trans>
            Hai una checklist DFM? Carica le regole di progettazione →
          </Trans>
        </button>
      ) : null}

      <div className="w-full max-w-2xl space-y-3 pt-2">
        <div className="flex items-center justify-center gap-2 font-medium text-[10px] text-ink-3 uppercase tracking-[0.1em]">
          <span aria-hidden className="h-px w-8 bg-hairline" />
          <Trans>Come funziona</Trans>
          <span aria-hidden className="h-px w-8 bg-hairline" />
        </div>
        <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {WORKFLOW_STEPS.map((step, idx) => {
            const Icon = step.icon;
            return (
              <li
                key={step.id}
                className="flex flex-col items-start gap-1.5 rounded-xl border border-hairline bg-card/60 p-3"
              >
                <div className="flex w-full items-center justify-between">
                  <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-ando-orange-soft text-ando-orange">
                    <Icon size={13} strokeWidth={1.75} aria-hidden />
                  </span>
                  <span className="font-semibold text-[10px] text-ink-3 tabular-nums">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <span className="font-medium text-[12px] text-ink-1">
                  {i18n._(step.title)}
                </span>
                <span className="text-[10px] text-ink-3 leading-snug">
                  {typeof step.body === "string" ? step.body : i18n._(step.body)}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
DxfEmptyHero.displayName = "DxfEmptyHero";


// ─────────────────────────────────────────────────────────────────────
// Blueprint illustration — inline SVG with `currentColor` so it inherits
// the surrounding text token (no asset, no fetch, no theme drift).
// ─────────────────────────────────────────────────────────────────────


function BlueprintIllustration() {
  return (
    <div
      aria-hidden
      className="aspect-[3/2] w-60 max-w-full text-ink-3 sm:w-72"
    >
      <svg
        viewBox="0 0 240 160"
        width="100%"
        height="100%"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Page outline */}
        <rect
          x="6"
          y="6"
          width="228"
          height="148"
          rx="6"
          stroke="currentColor"
          strokeOpacity="0.25"
          strokeWidth="1"
        />

        {/* Title block (bottom-right) */}
        <rect
          x="158"
          y="120"
          width="68"
          height="26"
          stroke="currentColor"
          strokeOpacity="0.25"
          strokeWidth="1"
        />
        <line
          x1="180"
          y1="120"
          x2="180"
          y2="146"
          stroke="currentColor"
          strokeOpacity="0.25"
          strokeWidth="1"
        />

        {/* Background grid (subtle dotted) */}
        <g stroke="currentColor" strokeOpacity="0.08" strokeWidth="0.5">
          {[24, 48, 72, 96, 120, 144, 168, 192, 216].map((x) => (
            <line key={`v${x}`} x1={x} y1="6" x2={x} y2="154" />
          ))}
          {[30, 54, 78, 102, 126, 150].map((y) => (
            <line key={`h${y}`} x1="6" y1={y} x2="234" y2={y} />
          ))}
        </g>

        {/* Mechanical bracket — main feature */}
        <g
          stroke="currentColor"
          strokeOpacity="0.85"
          strokeWidth="1.5"
          fill="none"
        >
          {/* Outer L-bracket profile */}
          <path d="M 36 38 L 36 96 L 110 96 L 110 78 L 56 78 L 56 38 Z" />

          {/* Top mounting holes (circles with center crosshair) */}
          <circle cx="46" cy="50" r="3.5" />
          <line x1="42.5" y1="50" x2="49.5" y2="50" strokeWidth="0.6" />
          <line x1="46" y1="46.5" x2="46" y2="53.5" strokeWidth="0.6" />

          <circle cx="46" cy="66" r="3.5" />
          <line x1="42.5" y1="66" x2="49.5" y2="66" strokeWidth="0.6" />
          <line x1="46" y1="62.5" x2="46" y2="69.5" strokeWidth="0.6" />

          {/* Right-arm slot */}
          <rect x="68" y="84" width="32" height="6" rx="3" />
        </g>

        {/* Isometric small cube (top-right detail view) */}
        <g
          stroke="currentColor"
          strokeOpacity="0.55"
          strokeWidth="1"
          fill="none"
        >
          <polygon points="184,48 200,40 216,48 200,56" />
          <polygon points="184,48 200,56 200,76 184,68" />
          <polygon points="216,48 200,56 200,76 216,68" />
        </g>

        {/* Dimension lines */}
        <g
          stroke="currentColor"
          strokeOpacity="0.45"
          strokeWidth="0.75"
          strokeDasharray="2 2"
        >
          {/* Vertical dim (left side) */}
          <line x1="22" y1="38" x2="22" y2="96" strokeDasharray="0" />
          <line x1="20" y1="38" x2="24" y2="38" strokeDasharray="0" />
          <line x1="20" y1="96" x2="24" y2="96" strokeDasharray="0" />

          {/* Horizontal dim (bottom) */}
          <line x1="36" y1="108" x2="110" y2="108" strokeDasharray="0" />
          <line x1="36" y1="106" x2="36" y2="110" strokeDasharray="0" />
          <line x1="110" y1="106" x2="110" y2="110" strokeDasharray="0" />
        </g>

        {/* Dimension labels (small text) */}
        <g
          fill="currentColor"
          fillOpacity="0.55"
          stroke="none"
          fontFamily="ui-monospace, SFMono-Regular, monospace"
          fontSize="6.5"
        >
          <text x="14" y="69" textAnchor="middle">
            58
          </text>
          <text x="73" y="116" textAnchor="middle">
            74
          </text>
        </g>

        {/* Centerline through brackets */}
        <line
          x1="6"
          y1="67"
          x2="234"
          y2="67"
          stroke="currentColor"
          strokeOpacity="0.18"
          strokeWidth="0.5"
          strokeDasharray="6 2 1 2"
        />

        {/* Drawing scale marker (top-left corner) */}
        <g
          fill="currentColor"
          fillOpacity="0.4"
          stroke="none"
          fontFamily="ui-monospace, SFMono-Regular, monospace"
          fontSize="6"
        >
          <text x="12" y="16">
            SCALE 1:1
          </text>
        </g>
      </svg>
    </div>
  );
}
